import { useEffect, useMemo, useState } from 'react';

export type IncompleteRequiredRoomGroup = {
  room: string;
  items: string[];
};

const PAGE_SIZE = 5;

type IncompleteRequiredInventoryListProps = {
  title: string;
  groups: IncompleteRequiredRoomGroup[];
  seeMoreLabel: string;
  className?: string;
};

export const IncompleteRequiredInventoryList = ({
  title,
  groups,
  seeMoreLabel,
  className,
}: IncompleteRequiredInventoryListProps) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const flatItems = useMemo(
    () =>
      groups.flatMap((group) =>
        group.items.map((item) => ({
          room: group.room,
          item,
        })),
      ),
    [groups],
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [flatItems.length]);

  const visibleItems = flatItems.slice(0, visibleCount);
  const remaining = Math.max(0, flatItems.length - visibleCount);

  const visibleGroups = useMemo(() => {
    const map = new Map<string, string[]>();
    visibleItems.forEach(({ room, item }) => {
      const current = map.get(room) ?? [];
      current.push(item);
      map.set(room, current);
    });
    return Array.from(map.entries()).map(([room, items]) => ({ room, items }));
  }, [visibleItems]);

  if (flatItems.length === 0) return null;

  return (
    <div className={className}>
      <p className="m-0 font-semibold">
        {title} ({flatItems.length})
      </p>
      <ul className="m-0 mt-2 list-none space-y-2 p-0">
        {visibleGroups.map((group) => (
          <li key={group.room}>
            <p className="m-0 font-bold text-amber-800">{group.room}</p>
            <ul className="m-0 mt-1 list-none space-y-0.5 p-0 pl-2">
              {group.items.map((item) => (
                <li key={`${group.room}-${item}`} className="before:mr-1.5 before:content-['-']">
                  {item}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {remaining > 0 ? (
        <button
          type="button"
          className="mt-2 text-left text-sm font-bold text-[#0B3465] underline"
          onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
        >
          {seeMoreLabel.replace('{count}', String(Math.min(PAGE_SIZE, remaining)))}
        </button>
      ) : null}
    </div>
  );
};
